/**
 * Activity Utilities
 *
 * Shared utility functions for emissions activity data display.
 */

import { ActivityUnit } from '../types/emissions';
import type { ActivityRecord } from '../types/emissions';
import { FUEL_TYPE_LABELS, VEHICLE_TYPE_LABELS, GAS_TYPE_LABELS, UNIT_LABELS } from '../components/form/types';

/**
 * Climatiq's `unit_type` filter values (matches the server-side
 * `ClimatiqUnitType` union — only the four families we map units into).
 * Surfaced in the emissions-tracker app so the staging table can pass a
 * `unitType` to `FactorSearchModal` without an unrelated factor + unit
 * combination sneaking past the modal's filter.
 */
const MASS_UNITS: ReadonlySet<ActivityUnit> = new Set([
  ActivityUnit.Kilograms,
  ActivityUnit.Tonnes,
  ActivityUnit.Pounds,
  ActivityUnit.ShortTons,
]);
const ENERGY_UNITS: ReadonlySet<ActivityUnit> = new Set([
  ActivityUnit.KWh,
  ActivityUnit.MWh,
  ActivityUnit.GJ,
  ActivityUnit.MJ,
  ActivityUnit.Therms,
  ActivityUnit.MMBTU,
]);
const VOLUME_UNITS: ReadonlySet<ActivityUnit> = new Set([
  ActivityUnit.Litres,
  ActivityUnit.Gallons,
  ActivityUnit.CubicMeters,
  ActivityUnit.CubicFeet,
]);
const DISTANCE_UNITS: ReadonlySet<ActivityUnit> = new Set([
  ActivityUnit.Kilometers,
  ActivityUnit.Miles,
]);

/**
 * Maps an `ActivityUnit` to its Climatiq `unit_type` family. Mirrors
 * `getClimatiqUnitType` in `apps/emissions/src/util/climatiqUtils.ts` —
 * the source of truth lives server-side, but the staging-table picker
 * needs the same classification to keep the modal scoped to compatible
 * factors. Returns an empty array for area / count units (no Climatiq
 * unit_type maps to those) so callers can omit the filter rather than
 * pass a meaningless one.
 */
export const getClimatiqUnitType = (
  unit: ActivityUnit | '' | undefined | null,
): string[] => {
  if (!unit) return [];
  if (MASS_UNITS.has(unit as ActivityUnit)) return ['Weight'];
  if (ENERGY_UNITS.has(unit as ActivityUnit)) return ['Energy'];
  if (VOLUME_UNITS.has(unit as ActivityUnit)) return ['Volume'];
  if (DISTANCE_UNITS.has(unit as ActivityUnit)) return ['Distance'];
  return [];
};

/**
 * Display label for an ActivityUnit storage value.
 *
 * Storage values are aligned with Climatiq strings (e.g. 'l', 'gallon_us',
 * 'cubic_foot', 'MMBTU') which are unfriendly for users; this returns the
 * human-readable label from `@g17eco/emissions-config` ("Liters", "Gallons",
 * etc.). Falls back to the raw value for any unrecognised string so legacy
 * or future units never render as blank.
 */
export const getActivityUnitLabel = (unit: ActivityUnit | string | undefined | null): string => {
  if (!unit) return '';
  return UNIT_LABELS[unit as ActivityUnit] ?? unit;
};

export const getActivitySubtype = (
  activity: Pick<ActivityRecord, 'fuelType' | 'refrigerantType' | 'vehicleType'>
): string | null => {
  if (activity.fuelType) return FUEL_TYPE_LABELS[activity.fuelType] ?? activity.fuelType;
  if (activity.refrigerantType) return GAS_TYPE_LABELS[activity.refrigerantType] ?? activity.refrigerantType;
  if (activity.vehicleType) return VEHICLE_TYPE_LABELS[activity.vehicleType] ?? activity.vehicleType;
  return null;
};

/**
 * Formats time ago from a date string.
 */
/**
 * Gets the user name from a createdBy field.
 */
export const getUserName = (createdBy: { _id: string; name?: string } | string | undefined): string => {
  if (!createdBy) {
    return 'Unknown';
  }
  if (typeof createdBy === 'string') {
    return 'Unknown';
  }
  return createdBy.name || 'Unknown';
};

/**
 * Expand a `<input type="month">` value (`YYYY-MM`) into the ISO date range
 * the API expects (`YYYY-MM-DD`). The picker captures intent at month
 * granularity; without expansion the server stores both ends as the 1st and
 * treats the activity as zero-day (breaking period filters and proration).
 *
 * Passes through anything that already looks like `YYYY-MM-DD` so editing an
 * existing activity (or a future day-level picker) round-trips cleanly.
 */
export const expandMonthToPeriodRange = (
  periodStart: string,
  periodEnd: string,
): { periodStart: string; periodEnd: string } => ({
  periodStart: monthStart(periodStart),
  periodEnd: monthEnd(periodEnd),
});

const monthStart = (value: string): string => {
  if (!/^\d{4}-\d{2}$/.test(value)) {
    return value;
  }
  return `${value}-01`;
};

const monthEnd = (value: string): string => {
  if (!/^\d{4}-\d{2}$/.test(value)) {
    return value;
  }
  const [y, m] = value.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${value}-${String(lastDay).padStart(2, '0')}`;
};
